import { jrStaffUidAliases, type JrStaffSource } from './jrStaffRoster.ts';
import { liveBoardSubRequests } from './subRequestVisibility.ts';
import { teacherDisplayName, teacherLabelForStaffRow } from './teacherTitle.ts';
import type { UserDoc } from './users.ts';

export interface AdminSubBoardRequestShape {
  id: string;
  status?: string;
  date?: string;
  requesterUid: string;
  claimedBy?: string | null;
  notes?: string | null;
  recordedCover?: unknown;
}

export interface AdminSubBoardDecoratedRow {
  request: AdminSubBoardRequestShape;
  className: string;
  timeLabel: string;
  urgencyHours: number;
}

export interface AdminConfirmedSubRow {
  id: string;
  className: string;
  dateLabel: string;
  timeLabel: string;
  requesterLabel: string;
  claimerLabel: string;
  claimerIsJrStaff: boolean;
  notes: string;
}

/**
 * A claimed record without a claimer is incomplete, not confirmed cover. A
 * row a Director wrote down by hand is dropped too - see
 * subRequestVisibility.ts - so a same-day recorded cover never shows up on
 * the Admin desk's "tonight" card as if it were live news.
 */
export function confirmedSubCoversForDate<
  T extends { status?: string; date?: string; claimedBy?: string | null; recordedCover?: unknown }
>(requests: readonly T[], dateKey: string): T[] {
  return liveBoardSubRequests(requests).filter((request) => (
    request.status === 'claimed' && !!request.claimedBy && request.date === dateKey
  ));
}

function personLabel(
  uid: string,
  usersByUid: ReadonlyMap<string, UserDoc>,
  aliases: ReadonlyMap<string, string>
): string {
  const canonicalUid = aliases.get(uid) ?? uid;
  const user = usersByUid.get(canonicalUid) ?? usersByUid.get(uid);
  return user
    ? teacherDisplayName(user)
    : teacherLabelForStaffRow(null, null, canonicalUid);
}

/**
 * Maps the Admin board's already decorated requests into display rows.
 * This stays outside React so the one-human alias rule and Jr Staff pill are
 * executed by tests for every account shape instead of being pinned only by
 * source text.
 */
export function buildAdminConfirmedSubRows(input: {
  decorated: readonly AdminSubBoardDecoratedRow[];
  dateKey: string;
  users: readonly UserDoc[];
  designations: JrStaffSource['designations'];
  formatDate?: (dateKey: string) => string;
}): AdminConfirmedSubRow[] {
  const aliases = jrStaffUidAliases({
    users: input.users,
    designations: input.designations,
  });
  const usersByUid = new Map(input.users.map((user) => [user.uid, user]));
  const confirmedIds = new Set(
    confirmedSubCoversForDate(
      input.decorated.map((row) => row.request),
      input.dateKey
    ).map((request) => request.id)
  );
  const formatDate = input.formatDate ?? ((dateKey: string) => dateKey);

  return input.decorated
    .filter((row) => confirmedIds.has(row.request.id))
    .slice()
    .sort((a, b) => a.urgencyHours - b.urgencyHours || (a.request.date ?? '').localeCompare(b.request.date ?? ''))
    .map((row) => {
      const claimerUid = row.request.claimedBy as string;
      return {
        id: row.request.id,
        className: row.className,
        dateLabel: formatDate(row.request.date ?? ''),
        timeLabel: row.timeLabel,
        requesterLabel: personLabel(row.request.requesterUid, usersByUid, aliases),
        claimerLabel: personLabel(claimerUid, usersByUid, aliases),
        claimerIsJrStaff: aliases.has(claimerUid),
        notes: row.request.notes ?? '',
      };
    });
}
